/**
 * Force-Directed Network Visualization
 * Now includes multiple chart options from Observable HQ patterns
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
// Import chart components
import DisjointForceGraph from './charts/DisjointForceGraph';
import ArcDiagram from './charts/ArcDiagram';
import PatentSuitsGraph from './charts/PatentSuitsGraph';
import RadialCluster from './charts/RadialCluster';
import TreeOfLife from './charts/TreeOfLife';

// For backward compatibility, also import the comprehensive MegaTinyCo analysis
import NetworkView from '../NetworkView';

const CHART_OPTIONS = {
  'circular': {
    label: 'Circular Network',
    description: 'Nodes arranged in circular pattern with colored relationship lines',
    type: 'basic',
  },
  'clusters': {
    label: 'Team Clusters',
    description: 'Grouped by organizational teams with cluster backgrounds',
    type: 'basic',
  },
  'matrix': {
    label: 'Relationship Matrix',
    description: 'Heatmap showing trust levels between all individuals',
    type: 'basic',
  },
  'hierarchical': {
    label: 'Organizational Tree',
    description: 'Traditional org chart based on reporting relationships',
    type: 'basic',
  },
  'stable-force': {
    label: 'Interactive Force Graph',
    description: 'Draggable network with metric-based coloring and stable physics',
    type: 'interactive',
  },
  'disjoint-force': {
    label: 'Team-Based Force Graph',
    description: 'Separate clusters showing team groupings with force simulation',
    component: DisjointForceGraph,
    type: 'algorithmic',
  },
  'arc-diagram': {
    label: 'Arc Diagram',
    description: 'Linear arrangement with curved relationship arcs',
    component: ArcDiagram,
    type: 'algorithmic',
  },
  'patent-suits': {
    label: 'Strength Clustering',
    description: 'Groups nodes by relationship strength (strong/moderate/weak)',
    component: PatentSuitsGraph,
    type: 'algorithmic',
  },
  'radial-cluster': {
    label: 'Radial Hierarchy',
    description: 'Circular hierarchical layout by entity type',
    component: RadialCluster,
    type: 'algorithmic',
  },
  'tree-of-life': {
    label: 'Centrality Tree',
    description: 'Tree structure based on relationship centrality',
    component: TreeOfLife,
    type: 'algorithmic',
  },
};

function ForceDirectedNetwork() {
  const [selectedChart, setSelectedChart] = useState('circular');

  const currentChart = CHART_OPTIONS[selectedChart];

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Chart Selector */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
        elevation={1}
      >
        <FormControl size="small" sx={{ minWidth: 400 }}>
          <InputLabel>Visualization Type</InputLabel>
          <Select
            value={selectedChart}
            onChange={(e) => setSelectedChart(e.target.value)}
            label="Visualization Type"
          >
            {Object.entries(CHART_OPTIONS).map(([key, option]) => (
              <MenuItem key={key} value={key}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem />

        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {currentChart.description}
        </Typography>
      </Paper>

      {/* Chart Container */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <NetworkView
          externalVisualization={selectedChart}
          onVisualizationChange={(newViz) => setSelectedChart(newViz)}
        />
      </Box>
    </Box>
  );
}

export default React.memo(ForceDirectedNetwork);